import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Drag-to-reorder places on the Today tab.
//
// Wraps SortableJS around the place list. On drop, sends the new order to
// the server which assigns hourly schedule_at slots (9am, 10am, 11am...).
// Also updates the map route immediately by rewriting the map-places JSON.
//
// Mount on the place list container:
//   data-controller="field-today-sort"
//   data-field-today-sort-url-value="/field/today/reorder"
//
// Each sortable item needs data-place-id on the wrapper div.
// Drag handles need data-sort-handle attribute.

export default class extends Controller {
  static values = { url: String }

  connect() {
    this._sortable = Sortable.create(this.element, {
      handle:    "[data-sort-handle]",
      animation: 150,
      ghostClass: "opacity-40",
      // Card #4516 -- Completed rows have no drag handle (so they can never
      // start a drag) but, being plain siblings in the same list, SortableJS
      // would otherwise still accept them as a drop target between/after.
      // Reject any move that would land next to one.
      onMove:    (evt) => !evt.related?.closest?.("[data-field-today-completed]"),
      onEnd: () => this._onDrop()
    })
  }

  disconnect() {
    this._sortable?.destroy()
    this._sortable = null
  }

  _onDrop() {
    // Completed rows are never reorderable (card #4516) -- excluded here too,
    // belt and braces alongside the onMove guard above.
    const items    = this.element.querySelectorAll("[data-place-id]:not([data-field-today-completed])")
    const placeIds = Array.from(items).map(el => el.dataset.placeId).filter(Boolean)
    if (placeIds.length === 0) return

    // Update the map places JSON immediately so the route redraws in new order
    this._updateMapOrder(placeIds)

    // Notify the map to re-draw route
    window.dispatchEvent(new CustomEvent("field-today-sort:reordered"))

    // Persist to server in the background (online only)
    if (navigator.onLine) this._save(placeIds)
  }

  async _save(placeIds) {
    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    try {
      await fetch(this.urlValue, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
        body:    JSON.stringify({ place_ids: placeIds })
      })
    } catch {
      // Silent fail -- order reverts on next page load
    }
  }

  _updateMapOrder(placeIds) {
    const el = document.getElementById("today-map-places")
    if (!el) return
    try {
      const places   = JSON.parse(el.dataset.places) || []
      const byId     = new Map(places.map(p => [String(p.id), p]))
      const reordered = placeIds.map(id => byId.get(id)).filter(Boolean)
      el.dataset.places = JSON.stringify(reordered)
    } catch { /* ignore */ }
  }
}
